/**
 * The family, its children, and which child profile is currently on screen.
 *
 * Two separate ideas that are easy to conflate: the *session* is the guardian's
 * and never changes when a child picks their avatar; the *profile* is a lens on
 * that session. Switching profiles changes what is displayed and what the app
 * will offer to do — it never changes who the server thinks is asking.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from './supabase.js';
import { useAuth } from './auth.jsx';
import { hashPin, newPinSalt } from './pin.js';

const FamilyContext = createContext(null);

export function FamilyProvider({ children }) {
  const { user } = useAuth();
  const [family, setFamily] = useState(null);
  const [kids, setKids] = useState([]);
  const [profileId, setProfileId] = useState(null); // null = the parent's view
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!user) { setFamily(null); setKids([]); setLoading(false); return; }
    setLoading(true);
    const { data: memberships, error: mErr } = await supabase
      .from('family_members')
      .select('family_id, families(id, name, timezone, house_rules)')
      .eq('user_id', user.id);

    if (mErr) { setError(mErr.message); setLoading(false); return; }

    const first = memberships?.[0]?.families ?? null;
    setFamily(first);

    if (first) {
      const { data: childRows, error: cErr } = await supabase
        .from('children')
        .select('id, display_name, avatar, birth_month, birth_year, pin_hash, archived_at')
        .eq('family_id', first.id)
        .is('archived_at', null)
        .order('created_at');
      if (cErr) setError(cErr.message); else setKids(childRows ?? []);
    } else {
      setKids([]);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const value = useMemo(() => ({
    family,
    kids,
    loading,
    error,
    reload: load,

    /** The child whose view is on screen, or null for the parent's own view. */
    profile: kids.find((k) => k.id === profileId) ?? null,
    switchToChild: (id) => setProfileId(id),
    switchToParent: () => setProfileId(null),

    createFamily: async (name, timezone) => {
      const { data, error: e } = await supabase.rpc('create_family', {
        family_name: name,
        family_timezone: timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      if (e) throw new Error(e.message);
      await load();
      return data;
    },

    /**
     * Cut an invite code for a second adult.
     *
     * The row is a request, not the invite: the code, the expiry, and whose
     * name is on it are all written by the database (see the before-insert
     * trigger in 0005). Anything this client sent for those columns would be
     * discarded, so it sends nothing and reads back what it was given.
     */
    createInvite: async () => {
      const { data, error: e } = await supabase
        .from('guardian_invites')
        .insert({ family_id: family.id })
        .select('id, code, expires_at, created_at')
        .single();
      if (e) throw new Error(e.message);
      return data;
    },

    /** Live invites, newest first — an unused code that hasn't expired yet. */
    listInvites: async () => {
      const { data, error: e } = await supabase
        .from('guardian_invites')
        .select('id, code, expires_at, created_at, accepted_at')
        .eq('family_id', family.id)
        .is('accepted_at', null)
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false });
      if (e) throw new Error(e.message);
      return data ?? [];
    },

    /** Destroy an unused code. Only unused ones will go — see the policy. */
    revokeInvite: async (id) => {
      const { error: e } = await supabase.from('guardian_invites').delete().eq('id', id);
      if (e) throw new Error(e.message);
    },

    /**
     * Redeem a code and become a guardian of that family.
     *
     * Deliberately not a plain insert into `family_members`: no policy would
     * allow it, because the caller is not a member yet. The whole exception
     * lives in one SECURITY DEFINER function whose gate is the code.
     */
    acceptInvite: async (code) => {
      const { data, error: e } = await supabase.rpc('accept_guardian_invite', {
        invite_code: String(code ?? '').trim().toLowerCase(),
      });
      if (e) throw new Error(e.message);
      await load();
      return data;
    },

    /**
     * The family's own words about how they spend, which a child reads on their
     * wishlist before they ask. Plain text on the family row rather than a
     * settings table: there is one of them, it has no history worth keeping,
     * and the sentence belongs to the household rather than to a feature.
     */
    setHouseRules: async (text) => {
      const { error: e } = await supabase
        .from('families')
        .update({ house_rules: text ?? null })
        .eq('id', family.id);
      if (e) throw new Error(e.message);
      await load();
    },

    addChild: async ({ displayName, avatar, birthMonth, birthYear, pin }) => {
      const pinHash = pin ? await hashPin(pin, newPinSalt()) : null;
      const { error: e } = await supabase.from('children').insert({
        family_id: family.id,
        display_name: displayName,
        avatar,
        // Month and year. Never an age — see src/domain/age.js.
        birth_month: birthMonth,
        birth_year: birthYear,
        pin_hash: pinHash,
      });
      if (e) throw new Error(e.message);
      await load();
    },

    /**
     * Edit a child. `pin` has three states and they are not the same thing:
     * undefined leaves the PIN alone, null removes it, a string sets a new one.
     * Collapsing "not mentioned" and "cleared" into one falsy value is how an
     * edit to a birth month quietly unlocks a profile.
     */
    updateChild: async (id, { displayName, avatar, birthMonth, birthYear, pin }) => {
      const patch = {
        display_name: displayName,
        avatar,
        birth_month: birthMonth,
        birth_year: birthYear,
      };
      if (pin !== undefined) patch.pin_hash = pin === null ? null : await hashPin(pin, newPinSalt());

      const { error: e } = await supabase
        .from('children')
        .update(patch)
        .eq('id', id)
        .eq('family_id', family.id);
      if (e) throw new Error(e.message);
      await load();
    },

    /**
     * Removing a child is an archive, never a delete — there is no delete policy
     * on `children` on purpose. The ledger keeps referring to them, so the row
     * has to stay; what changes is that they drop out of the crew.
     */
    archiveChild: async (id) => {
      const { error: e } = await supabase
        .from('children')
        .update({ archived_at: new Date().toISOString() })
        .eq('id', id)
        .eq('family_id', family.id);
      if (e) throw new Error(e.message);
      // Don't leave the app showing a profile that no longer exists.
      if (profileId === id) setProfileId(null);
      await load();
    },
  }), [family, kids, profileId, loading, error, load]);

  return <FamilyContext.Provider value={value}>{children}</FamilyContext.Provider>;
}

export function useFamily() {
  const ctx = useContext(FamilyContext);
  if (!ctx) throw new Error('useFamily outside FamilyProvider');
  return ctx;
}
